import { useRef } from "react";

import StoredFile from "@/components/StoredFile";
import { Upload, X, FileCheck, FileText, Paperclip, MapPin } from "lucide-react";

export default function UploadDocumentsSection({
  // Acceptance PDF
  acceptancePdfTemplate, uploadingAcceptance, isDraggingAcceptance,
  handleAcceptanceDragOver, handleAcceptanceDragLeave, handleAcceptanceDrop,
  handleUploadAcceptancePDF, handleDeleteAcceptancePDF, setPreviewPDF,
  // Postage Statement
  postageStatementTemplate, uploadingPostageStatement, isDraggingPostageStatement,
  handlePostageStatementDragOver, handlePostageStatementDragLeave, handlePostageStatementDrop,
  handleUploadPostageStatementPDF, handleDeletePostageStatementPDF,
  postageStatementAddress, setShowAddressModal,
  // Additional Documents
  additionalDocuments, uploadingDocs, isDragging,
  handleDragOver, handleDragLeave, handleDrop,
  handleUploadDocuments, handleDeleteDocument
}) {
  const acceptanceFileInputRef = useRef(null);
  const postageStatementFileInputRef = useRef(null);
  const fileInputRef = useRef(null);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1.5rem", marginBottom: "1.5rem" }}>
      {/* Column 1: Acceptance PDF */}
      <div className="form-section" style={{ marginBottom: 0 }}>
        <div className="form-title" style={{ fontSize: "0.95rem" }}>
          <FileCheck size={18} style={{ display: "inline", marginRight: "0.5rem" }} />
          Acceptance PDF
        </div>
        <p style={{ color: "#64748b", fontSize: "0.75rem", marginBottom: "1rem" }}>USPS Confirmation Acceptance form template</p>
        {acceptancePdfTemplate ? (
          /*
           * A stored file is an object path in a private bucket, not a URL.
           * This showed a fixed "PDF Uploaded" and handed that path to the
           * viewer as though it were one, so nothing opened and the file name
           * was never shown. StoredFile signs the path and offers the file by
           * name, to view or to download.
           */
          <StoredFile
            path={acceptancePdfTemplate}
            label="Acceptance form"
            onRemove={() => {
              if (confirm("Remove Acceptance PDF?")) handleDeleteAcceptancePDF();
            }}
          />
        ) : (
          <>
            <input ref={acceptanceFileInputRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={(e) => { if (e.target.files?.[0]) { handleUploadAcceptancePDF(e.target.files[0]); e.target.value = ''; } }} />
            <div onDragOver={handleAcceptanceDragOver} onDragLeave={handleAcceptanceDragLeave} onDrop={handleAcceptanceDrop} onClick={() => acceptanceFileInputRef.current?.click()} style={{ border: isDraggingAcceptance ? "3px dashed #10b981" : "2px dashed #10b981", borderRadius: "8px", padding: "1.5rem 1rem", textAlign: "center", background: isDraggingAcceptance ? "#f0fdf4" : "#f8fafc", cursor: uploadingAcceptance ? "wait" : "pointer", transition: "all 0.3s ease", minHeight: "160px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Upload size={28} style={{ margin: "0 auto 0.5rem", color: isDraggingAcceptance ? "#10b981" : "#94a3b8" }} />
              <div style={{ fontSize: "0.8rem", fontWeight: 600, color: isDraggingAcceptance ? "#10b981" : "#475569", marginBottom: "0.25rem" }}>{uploadingAcceptance ? "Uploading..." : isDraggingAcceptance ? "Drop here" : "Upload PDF"}</div>
              <p style={{ fontSize: "0.7rem", color: "#64748b" }}>Drag &amp; drop or click</p>
            </div>
          </>
        )}
      </div>

      {/* Column 2: Postage Statement PDF */}
      <div className="form-section" style={{ marginBottom: 0 }}>
        <div className="form-title" style={{ fontSize: "0.95rem" }}>
          <FileText size={18} style={{ display: "inline", marginRight: "0.5rem" }} />
          Postage Statement
        </div>
        <p style={{ color: "#64748b", fontSize: "0.75rem", marginBottom: "1rem" }}>Template with address overlay</p>
        {postageStatementTemplate ? (
          <div style={{ marginBottom: 0 }}>
            <div style={{ marginBottom: "0.5rem" }}>
              <StoredFile
                path={postageStatementTemplate}
                label="Postage statement"
                onRemove={() => {
                  if (confirm("Remove Postage Statement PDF?")) handleDeletePostageStatementPDF();
                }}
              />
            </div>
            {postageStatementAddress ? (
              <div style={{ padding: "0.75rem", background: "#f0fdf4", border: "2px solid #10b981", borderRadius: "8px" }}>
                <div style={{ fontWeight: 600, color: "#065f46", marginBottom: "0.25rem", fontSize: "0.75rem" }}><MapPin size={12} style={{ display: "inline", marginRight: "0.25rem" }} />Address Set</div>
                <div style={{ fontSize: "0.7rem", color: "#064e3b", marginBottom: "0.5rem" }}>{postageStatementAddress.name}<br />{postageStatementAddress.address}<br />{postageStatementAddress.city}, {postageStatementAddress.state} {postageStatementAddress.zip}</div>
                <button className="btn btn-sm btn-outline" onClick={() => setShowAddressModal(true)} style={{ fontSize: "0.7rem", padding: "0.375rem", width: "100%" }}>Edit Address</button>
              </div>
            ) : (
              <button className="btn btn-outline" onClick={() => setShowAddressModal(true)} style={{ width: "100%", fontSize: "0.75rem", padding: "0.5rem" }}>
                <MapPin size={14} /> Add Address
              </button>
            )}
          </div>
        ) : (
          <>
            <input ref={postageStatementFileInputRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={(e) => { if (e.target.files?.[0]) { handleUploadPostageStatementPDF(e.target.files[0]); e.target.value = ''; } }} />
            <div onDragOver={handlePostageStatementDragOver} onDragLeave={handlePostageStatementDragLeave} onDrop={handlePostageStatementDrop} onClick={() => postageStatementFileInputRef.current?.click()} style={{ border: isDraggingPostageStatement ? "3px dashed #3b82f6" : "2px dashed #3b82f6", borderRadius: "8px", padding: "1.5rem 1rem", textAlign: "center", background: isDraggingPostageStatement ? "#eff6ff" : "#f8fafc", cursor: uploadingPostageStatement ? "wait" : "pointer", transition: "all 0.3s ease", minHeight: "160px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Upload size={28} style={{ margin: "0 auto 0.5rem", color: isDraggingPostageStatement ? "#3b82f6" : "#94a3b8" }} />
              <div style={{ fontSize: "0.8rem", fontWeight: 600, color: isDraggingPostageStatement ? "#3b82f6" : "#475569", marginBottom: "0.25rem" }}>{uploadingPostageStatement ? "Uploading..." : isDraggingPostageStatement ? "Drop here" : "Upload PDF"}</div>
              <p style={{ fontSize: "0.7rem", color: "#64748b" }}>Drag &amp; drop or click</p>
            </div>
          </>
        )}
      </div>

      {/* Column 3: Additional Documents */}
      <div className="form-section" style={{ marginBottom: 0 }}>
        <div className="form-title" style={{ fontSize: "0.95rem" }}>
          <Paperclip size={18} style={{ display: "inline", marginRight: "0.5rem" }} />
          Additional Docs
        </div>
        <p style={{ color: "#64748b", fontSize: "0.75rem", marginBottom: "1rem" }}>Extra documents (multi-upload)</p>
        {additionalDocuments.length > 0 && (
          <div style={{ marginBottom: "0.75rem", maxHeight: "120px", overflowY: "auto" }}>
            {additionalDocuments.map((doc, idx) => (
              <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.5rem", background: "#f0f9ff", border: "1px solid #bfdbfe", borderRadius: "6px", marginBottom: "0.25rem" }}>
                <button onClick={() => setPreviewPDF({ url: doc.url, title: doc.name, type: 'additional' })} style={{ background: "transparent", border: "none", color: "#3b82f6", cursor: "pointer", fontSize: "0.7rem", fontWeight: 600, textAlign: "left", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={doc.name}>📄 {doc.name}</button>
                <button onClick={() => { if (confirm(`Delete ${doc.name}?`)) handleDeleteDocument(idx); }} style={{ background: "transparent", border: "none", color: "#dc2626", cursor: "pointer", padding: "0.125rem" }}><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept=".pdf" multiple style={{ display: "none" }} onChange={(e) => { handleUploadDocuments(e.target.files); e.target.value = ''; }} />
        <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} style={{ border: isDragging ? "3px dashed #f59e0b" : "2px dashed #cbd5e1", borderRadius: "8px", padding: "1.5rem 1rem", textAlign: "center", background: isDragging ? "#fffbeb" : "#f8fafc", cursor: uploadingDocs ? "wait" : "pointer", transition: "all 0.3s ease", minHeight: "160px", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Upload size={28} style={{ margin: "0 auto 0.5rem", color: isDragging ? "#f59e0b" : "#94a3b8" }} />
          <div style={{ fontSize: "0.8rem", fontWeight: 600, color: isDragging ? "#f59e0b" : "#475569", marginBottom: "0.25rem" }}>{uploadingDocs ? "Uploading..." : isDragging ? "Drop here" : "Upload PDFs"}</div>
          <p style={{ fontSize: "0.7rem", color: "#64748b" }}>Multiple files OK</p>
        </div>
      </div>
    </div>
  );
}